// src/pages/AssetsLandingPage.tsx
// Assets Module landing page - hub for asset tracking features

import React from 'react';
import { Link } from 'react-router-dom';
import { Box, FolderOpen, ClipboardCheck, BarChart3, ArrowRight } from 'lucide-react';

const AssetsLandingPage: React.FC = () => {
  const userRole = localStorage.getItem('userRole') || '';
  const canManageCategories = userRole === 'SuperAdmin' || userRole === 'OrgAdmin';

  const navigationCards = [
    {
      title: 'Tracked Assets',
      description: 'View, add, and manage high-value tracked items requiring annual verification',
      href: '/assets/tracked',
      icon: Box,
      color: 'bg-blue-500',
      available: true
    },
    {
      title: 'Asset Categories',
      description: 'Configure asset categories and tracking requirements',
      href: '/assets/categories',
      icon: FolderOpen,
      color: 'bg-purple-500',
      available: canManageCategories
    },
    {
      title: 'Verifications',
      description: 'Manage verification sessions and track annual asset audits',
      href: '/assets/verifications',
      icon: ClipboardCheck,
      color: 'bg-green-500',
      available: true
    },
    {
      title: 'Asset Reports',
      description: 'Generate reports on asset status, verification compliance, and history',
      href: '/assets/reports',
      icon: BarChart3,
      color: 'bg-amber-500',
      available: true
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Box className="w-7 h-7 mr-3 text-blue-600" />
          Asset Tracking
        </h1>
        <p className="text-gray-600 mt-1">
          Track and verify high-value "small and attractive" items
        </p>
      </div>

      {/* Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {navigationCards.map((card) => {
          const IconComponent = card.icon;
          
          if (!card.available) {
            return null;
          }

          return (
            <Link
              key={card.title}
              to={card.href}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg hover:border-blue-300 transition-all duration-200 group"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className={`${card.color} p-3 rounded-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {card.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {card.description}
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          );
        })}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Box className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">About Asset Tracking</h4>
            <p className="text-sm text-blue-700 mt-1">
              The Asset Tracking module is designed for high-value items that require annual verification 
              and chain-of-custody documentation. Unlike quartermaster inventory, these items are tracked 
              individually regardless of assignment status.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetsLandingPage;